import { StarIcon } from "./icon-sprite"

interface StarRatingProps {
  /** Numeric rating (0–5) */
  rating: number
  /** Whether to show the numeric label next to stars (defaults to true) */
  showLabel?: boolean
  /** Additional classes on the wrapper */
  className?: string
}

/**
 * StarRating — 5-star rating display used in product grid cards.
 *
 * Renders filled/unfilled stars based on rating value with an optional
 * numeric label. Uses Amazon-style gold (#FFA41C) for filled stars.
 *
 * FRIDGE-149 §4 — the five stars are one `role="img"` named "N out of 5
 * stars", so assistive tech hears a rating (not five unlabeled graphics and
 * a bare number); the individual glyphs are aria-hidden. The visible numeral
 * is gray-600 (FRIDGE-141: gray-400 measured 2.60:1 on white).
 */
export function StarRating({ rating, showLabel = true, className = "" }: StarRatingProps) {
  const label = `${rating.toFixed(1)} out of 5 stars`
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="flex" role="img" aria-label={label}>
        {/* FRIDGE-179: each star references the shared <symbol> instead of
            carrying its own copy of the path. Same viewBox and currentColor,
            so this renders pixel-identically. StarIcon is aria-hidden itself. */}
        {[...Array(5)].map((_, i) => (
          <StarIcon
            key={i}
            className={`w-3 h-3 ${i < Math.floor(rating) ? "text-[#FFA41C]" : "text-gray-200"}`}
          />
        ))}
      </div>
      {/* The numeral repeats what the img already says — hidden from AT so
          the rating is announced once. */}
      {showLabel && <span className="text-[10px] text-black" aria-hidden="true">{rating.toFixed(1)}</span>}
    </div>
  )
}
